import axiosInstance from './axios/index';

export interface PartnerDTO {
  activeProjectCount: number;
  addressLine1?: string;
  allowClockInOut?: boolean;
  averageRating?: number;
  city?: string;
  companyName: string;
  contactEmail: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhone?: string;
  country?: string;
  createdAt: string;
  description?: string;
  hourlyRate?: number | string | null;
  id: string;
  notes?: string;
  proxyUserId?: string;
  skills?: any[];
  state?: string;
  status: string;
  totalProjectsAssigned?: number;
  totalProjectsCompleted?: number;
  totalRatings?: number;
  updatedAt: string;
  zipCode?: string;
}

export interface PartnerRatingDTO {
  comment?: string;
  createdAt: string;
  id: string;
  projectName?: string;
  ratedBy?: { firstName: string; lastName: string };
  rating: number;
}

export interface PartnerInviteDTO {
  acceptedAt?: string;
  companyName: string;
  createdAt: string;
  email: string;
  expiresAt: string;
  id: string;
  invitedBy?: { email: string; firstName: string; lastName: string };
  partner?: PartnerDTO;
  status: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    nextPage: number | null;
    previousPage: number | null;
    totalItems: number;
    totalPages: number;
  };
}

const getAll = async (): Promise<PartnerDTO[]> => {
  const response = await axiosInstance.get('/partner');
  return response.data;
};

const getPaginated = async (params: {
  items: number;
  page: number;
  status?: string;
}): Promise<PaginatedResponse<PartnerDTO>> => {
  const response = await axiosInstance.get('/partner/paginate', { params });
  return response.data;
};

const getById = async (id: string): Promise<PartnerDTO> => {
  const response = await axiosInstance.get(`/partner/${id}`);
  return response.data;
};

const update = async (
  id: string,
  data: Partial<PartnerDTO> & { skillIds?: string[] }
): Promise<PartnerDTO> => {
  const response = await axiosInstance.patch(`/partner/${id}`, data);
  return response.data;
};

const deletePartner = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/partner/${id}`);
};

const invite = async (data: {
  companyName: string;
  contactEmail: string;
}): Promise<{ inviteId: string; partnerId: string }> => {
  const response = await axiosInstance.post('/partner/invite', data);
  return response.data;
};

const getInvites = async (): Promise<PartnerInviteDTO[]> => {
  const response = await axiosInstance.get('/partner/invites');
  return response.data;
};

const approve = async (id: string): Promise<PartnerDTO> => {
  const response = await axiosInstance.post(`/partner/${id}/approve`);
  return response.data;
};

const reject = async (id: string): Promise<PartnerDTO> => {
  const response = await axiosInstance.post(`/partner/${id}/reject`);
  return response.data;
};

const suspend = async (id: string): Promise<PartnerDTO> => {
  const response = await axiosInstance.post(`/partner/${id}/suspend`);
  return response.data;
};

const reactivate = async (id: string): Promise<PartnerDTO> => {
  const response = await axiosInstance.post(`/partner/${id}/reactivate`);
  return response.data;
};

const createTechnician = async (
  id: string
): Promise<{ created: boolean; message: string }> => {
  const response = await axiosInstance.post(`/partner/${id}/create-technician`);
  return response.data;
};

// ── Public registration (no auth required) ──────────────────

export interface PartnerInviteValidation {
  companyName: string;
  email: string;
  tenantCompanyName: string;
  valid: boolean;
}

export interface PartnerRegistrationData {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone?: string;
  country?: string;
  description?: string;
  state?: string;
  zipCode?: string;
}

const validateInviteToken = async (
  token: string
): Promise<PartnerInviteValidation> => {
  const response = await axiosInstance.get(
    `/partner-registration/${token}/validate`
  );
  return response.data;
};

const completeRegistration = async (
  token: string,
  data: PartnerRegistrationData
): Promise<{ accessToken: string; partnerId: string }> => {
  const response = await axiosInstance.post(
    `/partner-registration/${token}`,
    data
  );
  return response.data;
};

// ── Document requirements ───────────────────────────────────

export interface DocumentRequirementDTO {
  createdAt: string;
  description?: string;
  documentType: string;
  id: string;
  isRequired: boolean;
  name: string;
}

export interface PartnerDocumentDTO {
  createdAt: string;
  documentType: string;
  expiresAt?: string;
  fileName: string;
  fileUrl: string;
  id: string;
  name: string;
  rejectionReason?: string;
  requirement?: DocumentRequirementDTO;
  reviewedAt?: string;
  reviewedBy?: { firstName: string; lastName: string };
  status: string;
}

export interface ComplianceStatus {
  compliant: boolean;
  missing: DocumentRequirementDTO[];
  pending: PartnerDocumentDTO[];
  rejected: PartnerDocumentDTO[];
}

const getDocumentRequirements = async (): Promise<DocumentRequirementDTO[]> => {
  const response = await axiosInstance.get(
    '/partner-documents/document-requirements'
  );
  return response.data;
};

const createDocumentRequirement = async (data: {
  description?: string;
  documentType: string;
  isRequired?: boolean;
  name: string;
}): Promise<DocumentRequirementDTO> => {
  const response = await axiosInstance.post(
    '/partner-documents/document-requirements',
    data
  );
  return response.data;
};

const updateDocumentRequirement = async (
  id: string,
  data: { description?: string; isRequired?: boolean; name?: string }
): Promise<DocumentRequirementDTO> => {
  const response = await axiosInstance.patch(
    `/partner-documents/document-requirements/${id}`,
    data
  );
  return response.data;
};

const deleteDocumentRequirement = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/partner-documents/document-requirements/${id}`);
};

const getPartnerDocuments = async (
  partnerId: string
): Promise<PartnerDocumentDTO[]> => {
  const response = await axiosInstance.get(
    `/partner-documents/${partnerId}/documents`
  );
  return response.data;
};

const reviewDocument = async (
  partnerId: string,
  documentId: string,
  data: { rejectionReason?: string; status: string }
): Promise<PartnerDocumentDTO> => {
  const response = await axiosInstance.post(
    `/partner-documents/${partnerId}/documents/${documentId}/review`,
    data
  );
  return response.data;
};

const getPartnerCompliance = async (
  partnerId: string
): Promise<ComplianceStatus> => {
  const response = await axiosInstance.get(
    `/partner-documents/${partnerId}/compliance`
  );
  return response.data;
};

const getPartnerRequirements = async (
  partnerId: string
): Promise<DocumentRequirementDTO[]> => {
  const response = await axiosInstance.get(
    `/partner-documents/${partnerId}/requirements`
  );
  return response.data;
};

const createPartnerRequirement = async (
  partnerId: string,
  data: {
    description?: string;
    documentType: string;
    isRequired?: boolean;
    name: string;
  }
): Promise<DocumentRequirementDTO> => {
  const response = await axiosInstance.post(
    `/partner-documents/${partnerId}/requirements`,
    data
  );
  return response.data;
};

const deletePartnerRequirement = async (
  partnerId: string,
  id: string
): Promise<void> => {
  await axiosInstance.delete(
    `/partner-documents/${partnerId}/requirements/${id}`
  );
};

const ratePartner = async (
  partnerId: string,
  data: { comment?: string; projectName?: string; rating: number }
): Promise<PartnerRatingDTO> => {
  const response = await axiosInstance.post(`/partner/${partnerId}/rate`, data);
  return response.data;
};

const getPartnerRatings = async (
  partnerId: string
): Promise<PartnerRatingDTO[]> => {
  const response = await axiosInstance.get(`/partner/${partnerId}/ratings`);
  return response.data;
};

const requestPortalAccess = async (
  email: string
): Promise<{ sent: boolean }> => {
  const response = await axiosInstance.post(
    '/partner-registration/request-portal-access',
    { email }
  );
  return response.data;
};

export default {
  approve,
  completeRegistration,
  createDocumentRequirement,
  createPartnerRequirement,
  createTechnician,
  deleteDocumentRequirement,
  deletePartner,
  deletePartnerRequirement,
  getAll,
  getById,
  getDocumentRequirements,
  getInvites,
  getPaginated,
  getPartnerCompliance,
  getPartnerDocuments,
  getPartnerRatings,
  getPartnerRequirements,
  invite,
  reactivate,
  ratePartner,
  reject,
  requestPortalAccess,
  reviewDocument,
  suspend,
  update,
  updateDocumentRequirement,
  validateInviteToken
};
